import Personnummer from 'personnummer';
import { AuthMethod, isIduraAuthMethod } from 'models/user';
import { isEidMethodEnabled } from 'utils/enabled-eid-methods';

/**
 * The login methods the user forms offer. Mirrors resolveAdminAuthMethod in
 * shared-denthub-code, which is what actually decides.
 *
 * Super admins (Denthub Support) may choose password or SITHS, may create users on the
 * number-bound e-IDs, and are the only route onto or off an e-ID for an existing user
 * (DD-3552); BankID may not be used to create or restore another credential, so users
 * cannot switch it themselves. Company SSO and integration users are managed elsewhere.
 */
const ADMIN_SELECTABLE_AUTH_METHODS: readonly AuthMethod[] = [AuthMethod.PASSWORD, AuthMethod.SITHS];

/** e-IDs Support can pre-register, because the login is matched on the person's number. */
export const PERSONAL_IDENTITY_NUMBER_AUTH_METHODS: readonly AuthMethod[] = [AuthMethod.BANKID_SE, AuthMethod.FTN_FI];

/**
 * e-IDs matched on a pseudonym that only exists after the person's first login, so an
 * account cannot be created on them. Listed, disabled, so Support can see why.
 */
export const NOT_PRE_REGISTRABLE_AUTH_METHODS: readonly AuthMethod[] = [AuthMethod.BANKID_NO, AuthMethod.MITID_DK];

const selectableAuthMethods = (): AuthMethod[] => [
    ...ADMIN_SELECTABLE_AUTH_METHODS,
    // An e-ID this build does not offer on the login page would be a user who can never log in.
    ...PERSONAL_IDENTITY_NUMBER_AUTH_METHODS.filter(isEidMethodEnabled),
];

export const isAuthMethodEditable = (current: AuthMethod | undefined): boolean =>
    !current || selectableAuthMethods().includes(current) || isIduraAuthMethod(current);

export type AuthMethodOption = { method: AuthMethod; disabled: boolean };

/** The selector's options: the choices, the current method when it is not one, and the e-IDs that cannot be created. */
export const adminAuthMethodOptions = (current: AuthMethod | undefined): AuthMethodOption[] => {
    const selectable = selectableAuthMethods();
    const methods = [...selectable, ...NOT_PRE_REGISTRABLE_AUTH_METHODS.filter(isEidMethodEnabled)];
    if (current && !methods.includes(current)) {
        methods.push(current);
    }
    return methods.map((method) => ({
        method,
        disabled: !selectable.includes(method) && method !== current,
    }));
};

export const requiresPersonalIdentityNumber = (method: AuthMethod | undefined): boolean =>
    !!method && PERSONAL_IDENTITY_NUMBER_AUTH_METHODS.includes(method);

// Finnish identity code: DDMMYY, century sign, NNN, check character (DDMMYYNNN mod 31).
const HETU_PATTERN = /^(\d{6})[-+YXWVUABCDEF](\d{3})([0-9A-FHJ-NPR-Y])$/;
const HETU_CHECK_CHARACTERS = '0123456789ABCDEFHJKLMNPRSTUVWXY';

const isValidFinnishIdentityCode = (value: string): boolean => {
    const match = HETU_PATTERN.exec(value.trim().toUpperCase());
    return !!match && HETU_CHECK_CHARACTERS[Number(`${match[1]}${match[2]}`) % 31] === match[3];
};

/** A light check for the form; the backend validates in full, dates included. */
export const isValidPersonalIdentityNumber = (method: AuthMethod | undefined, value: string | undefined): boolean => {
    if (!value) {
        return false;
    }
    return method === AuthMethod.FTN_FI ? isValidFinnishIdentityCode(value) : Personnummer.valid(value);
};
